import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageSquare, Clock, Lightbulb, ArrowRight } from 'lucide-react';

const commitments = [
  {
    icon: MessageSquare,
    title: 'Communication',
    desc: 'Making ourselves available to you 24 hours a day, 7 days a week, gives us the opportunity to get to know your legal issues in depth.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    desc: 'We present you with options that are viable under the law and provide valuable advice and litigation strategy that is unparalleled.',
  },
  {
    icon: Clock,
    title: 'Availability',
    desc: 'When you need trusted legal guidance from top lawyers, do not settle for anything less than the superior service we provide.',
  },
];

export default function CommitmentSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="commitment" data-testid="commitment-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Dark Background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #0B132B, #020617)' }} />
      <div className="absolute inset-0 opacity-20"
        style={{ background: 'radial-gradient(ellipse at bottom left, rgba(6,182,212,0.3), transparent 60%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-4 bg-white/10 text-cyan-bright">
            Our Promise
          </span>
          <h2 data-testid="commitment-heading" className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-white mb-4">
            Our Commitment to You
          </h2>
          <p className="max-w-2xl mx-auto font-body text-lg text-white/60 leading-relaxed">
            Communication, planning and innovation gives us the unique ability to anticipate
            outcomes and opportunities for you.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {commitments.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                data-testid={`commitment-card-${item.title.toLowerCase()}`}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="p-8 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-500 hover:border-cyan-bright/30 hover:shadow-[0_0_30px_rgba(6,182,212,0.15)]"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                  style={{ background: 'linear-gradient(135deg, #1D4ED8, #06B6D4)' }}
                >
                  <Icon size={22} className="text-white" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-3 tracking-tight">{item.title}</h3>
                <p className="font-body text-sm text-white/60 leading-relaxed">{item.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <p className="font-body text-base text-white/50 mb-8 max-w-xl mx-auto">
            Call us today to inquire about our many services. We can help you determine which
            practice area of the law your circumstances fall under and connect you with expert
            advice and legal solutions right away.
          </p>
          <button
            data-testid="commitment-cta"
            onClick={() => scrollTo('contact')}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-md text-white font-semibold transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #1D4ED8, #06B6D4)' }}
          >
            Get In Touch
            <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
